import { getDataReaderClient } from "@multiweb/clients/util";
import { IRowData } from "@multiweb/types/contracts";
import { EventsController } from "Global/Dom/EventsController";
import { IFulltextSearchCondition } from "UI/UI/UI/XGControls/Frames/FrameInterfaces";
import { ConditionParams, IDataSource, IDataSourceExtended, IDataSourceLoadInfo, IDataSourceLoadInfoBase, IDBDataSourceParams, IOrderBy, IUseDistinctDataSource, OrderType } from "../../../Global/Base/DataSources/Interfaces";
import { CaseInsensitiveMap } from "../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { IEncAttrValue } from "../../../Global/Base/DataTypes/IAttrValue";
import { Event } from "../../../Global/Base/Events";
import { InvalidOperationException } from "../../../Global/Base/Exceptions";
import { IDisposable } from "../../../Global/Base/IDisposable";
import { CancelError, CancellationToken, CancellationTokenSource } from "../../../Global/Base/Promises/CancellationTokenSource";
import { ConditionOperator, StringUtils } from "../../../Global/Base/Utils/StringUtils";
import { AttrValueReader } from "../../../OW.Remoting/Remoting/AttrValueReader";
import { IOWClassDescription } from "../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { ModelDescriptionsManager } from "../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { PasswordManager } from "../../../OW.Remoting/Remoting/PasswordManager";
import { RequestContext } from "../../../OW.Remoting/Remoting/RequestContext";
import { FrameContext } from "../../UI/UI/UIManagerInterfaces";
import { convertContextFromFrameToRequest } from "../../UI/Utilities/SharedUIMethods";
import { DATASOURCE_ALL_DATA } from "./constants";
import { IActualObjectChangeEventArgs } from "./EditableDBDataSource";

/** Konfigurace pro data source */
export interface IDataSourceParams {
    /** Název třídy */
    className: string;
    /** Seznam atributů (nepovinně) */
    attributes?: string[];
    /** Podmínka (nepovinně). Pokud není uvedeno použije se [ID] */
    condition?: string;
    /** Parametry podmínky (nepovinně) */
    conditionParams?: any[];
    /** Třídění (nepovinně) */
    orderBy?: IOrderBy[];
    /** Příznak jestli číst verzovaná data */
    readVersioned?: boolean;
    /** Počet záznamů, které mají být načteny v jedné dávce. Výchozí hodnota 50*/
    fetchCount?: number;
    /**
     * Maximální délka dat jednotlivých atributů (ořežou se), defaultně 0 (ořežou se na nakonfigurovanou hodnotu TrimLength v konfiguraci služby), -1 pro neomezeně
     */
    dataLength?: number;
    /** Kontext framu */
    frameContext: FrameContext;
}

/**
 * Trida reprezentujici databazovy zdroj dat pro konzumenta dat
 */
export class DBDataSource implements IDataSourceExtended, IUseDistinctDataSource, IDisposable {
    //#region Events
    /**
     * Vraci startIndex pro cteni dat
     */
    public dataLoaded: Event<IDataSource, IDataSourceLoadInfo> = new Event<IDataSource, IDataSourceLoadInfo>();

    public error: Event<DBDataSource, any> = new Event<DBDataSource, any>();

    /**
     * Pokud je zrušeno načítání dat
     */
    public loadingCancelled: Event<DBDataSource, any> = new Event<DBDataSource, IDataSourceLoadInfoBase>();

    /**
     * Pokud je spuštěno načítání dat. Parametrem je hodnota startIndex.
     */
    public loadingStarted: Event<DBDataSource, any> = new Event<DBDataSource, number | undefined>();

    /**
     * Pokud je ukončeno načítání dat
     */
    public loadingEnded: Event<DBDataSource, IDataSourceLoadInfoBase> = new Event<DBDataSource, IDataSourceLoadInfoBase>();

    /**
    * Vraci radek a zmeneny atribut
    */
    public readonly attributesChanged: Event<DBDataSource, IActualObjectChangeEventArgs> = new Event<DBDataSource, IActualObjectChangeEventArgs>();

    //#endregion Events

    //#region Fields
    private __classDescription: IOWClassDescription | null = null;

    protected _initializeCondition: string;

    protected _events = new EventsController();

    private __startIndex: number = 0;
    private _maxRecordCount?: number;
    protected get _startIndex(): number {
        return this.__startIndex;
    }
    protected set _startIndex(value: number) {
        this.__startIndex = value;
    }

    protected _batchSize: number;
    protected _dataLength: number;
    protected _condition: string = "";
    protected _allDataRead: boolean = false;
    protected _groupBy: string[] = null;
    protected _fullLengthAttributes: string[] = [];

    private __internalRows: readonly IRowData[];
    protected get _internalRows(): readonly IRowData[] {
        return this.__internalRows;
    }
    protected set _internalRows(value: readonly IRowData[]) {
        this.__internalRows = value;
    }

    protected _internalOrderBy: string[];
    protected _orderBy: IOrderBy[];
    /**
     * Informace o tom, zda se pridal automaticky DataKey do orderBy
     */
    private _orderByDataKeyAddedInternal: boolean = false;

    protected _className: string;
    protected _conditionParams: ConditionParams;
    protected _originalCondition: string;
    protected _filterCondition: string;
    protected _fulltextCondition: IFulltextSearchCondition;
    protected _readVersioned: boolean;
    protected _attributes: string[];
    protected _runningDataLoad: Promise<any>;
    protected _runningDataLoadCTS: CancellationTokenSource;

    protected _additionalConditions: CaseInsensitiveMap<string> = new CaseInsensitiveMap<string>();
    protected _additionalConditionsParam: CaseInsensitiveMap<ConditionParams> = new CaseInsensitiveMap<ConditionParams>();

    private _specificRecordWaitList: string[] = [];
    protected _frameContext: FrameContext | undefined = undefined;

    /**
     * Nastaví podmínku potřebnou pro načtení dat
     */
    public set condition(value: string) {
        this._originalCondition = value;
        this._updateCondition();
    }

    /**
     * Vrací kompletní podmínku se všemi nutnými částmi, která byla použita pro načtení dat
     */
    public get condition(): string {
        const condition = [this._condition];

        return StringUtils.combineConditionsArray(ConditionOperator.and, condition.concat(Array.from(this._additionalConditions.values())));
    }

    /**
    * Vrací podmínku potřebnou pro načtení dat
    */
    public getOriginalCondition(): string {
        return this._originalCondition;
    }

    /***
     * Nastaví vstupy pro podmínku
     */
    public set conditionParams(value: ConditionParams) {
        this._conditionParams = value;
    }

    /**
     * Vrací kompletní vstupy do podmínky, která byla použita pro načtení dat
     */
    public get conditionParams(): ConditionParams {
        let condParam = Array.from(this._conditionParams || []);

        for (const cp of this._additionalConditionsParam.values()) {
            condParam = condParam.concat(cp);
        }

        return condParam;
    }

    private _useDistinct = false;

    public get useDistinct(): boolean {
        return this._useDistinct;
    }

    public set useDistinct(v: boolean) {
        if (this._useDistinct === v) return;
        this._useDistinct = v;

        if (this._useDistinct && this._orderByDataKeyAddedInternal) {
            this._orderByDataKeyAddedInternal = false;
            this._internalOrderBy.splice(this._internalOrderBy.findIndex(item => item === this.dataKey), 1);
        }
    }
    /**
     * Nazev atributu pouziteho jako ID
     */
    public dataKey: string = "ID";

    //#endregion Fields

    public [Symbol.iterator](): Iterator<IRowData> {
        if (!this._internalRows)
            this._internalRows = new Array<IRowData>();

        return this._internalRows[Symbol.iterator]();
    }

    public get className(): string {
        return this._className;
    }

    //#region orderBy
    public get orderBy(): IOrderBy[] {
        return this._orderBy;
    }

    public set orderBy(values: IOrderBy[]) {
        this._orderBy = values;
        if (values && values.length > 0) {
            const resultOrderBy: string[] = [];
            let dataKeyPresent: boolean = false;
            const upperDataKey = this.dataKey.toLocaleUpperCase();

            for (const orderByItem of values) {
                resultOrderBy.push(this._parseOrderBy(orderByItem));
                if (orderByItem.attributeName.toLocaleUpperCase() === upperDataKey)
                    dataKeyPresent = true;
            }

            // Pokud není obsažen dataKey, přidá se. To zajistí stejné setřídění dat, pokud je v hodnotách nastavovaných atributů hodně opakujících se hodnot.
            if (!dataKeyPresent && !this.useDistinct) {
                resultOrderBy.push(this.dataKey);
                this._orderByDataKeyAddedInternal = true;
            }

            this._internalOrderBy = resultOrderBy;
        } else {
            if (!this.useDistinct) {
                this._internalOrderBy = [this.dataKey]; // při nezadaném sloupci pro třídění se defaultně použije sloupec ID
                this._orderByDataKeyAddedInternal = true;
            }
        }

        this.waitForReload().then(() => {
            if (this._internalRows && this._internalRows.length > 0)
                this.loadNewData(false);
        });
    }
    //#endregion orderBy

    //#region groupBy
    public get groupBy(): string[] {
        return this._groupBy;
    }

    public set groupBy(values: string[]) {
        this._groupBy = values;

        this.waitForReload().then(() => {
            if (this._internalRows && this._internalRows.length > 0)
                this.loadNewData();
        });
    }
    //#endregion groupBy

    //#region Constructors
    constructor({
        className,
        attributes,
        condition,
        conditionParams,
        orderBy,
        readVersioned = false,
        batchSize = 50,
        dataLength = 0,
        maxRecordCount,
        frameContext,
    }: IDBDataSourceParams) {
        this._className = className;
        this._attributes = attributes || ["ID"];
        this._initializeCondition = "ID <> 0";
        this._filterCondition = "";
        this._fulltextCondition = {};
        this.condition = condition;
        this._conditionParams = conditionParams;
        this.orderBy = orderBy;
        this._readVersioned = readVersioned;
        this._batchSize = batchSize;
        this._dataLength = dataLength;
        this._maxRecordCount = maxRecordCount;
        this._frameContext = frameContext;

        this._events.add(PasswordManager.addedPassword, async () => {
            this._decryptData(this._internalRows, await this._getClassDescription(), true);
        });
    }
    //#endregion Constructors

    /**
     * Převede IOrderBy do formátu vhodného pro DB dotaz
     * @param orderBy Data ke zpracování
     */
    protected _parseOrderBy(orderBy: IOrderBy): string {
        const order = orderBy.additionalOrdering ? `${orderBy.additionalOrdering} ${orderBy.attributeName}` : orderBy.attributeName; // `
        return orderBy.order === OrderType.descending ? `DESC ${order}` : order; // `
    }

    protected *_getIterator(startIndex: number, count?: number): IterableIterator<IRowData> {
        if (this._internalRows) {
            const maxIndex = count && count !== DATASOURCE_ALL_DATA ? Math.min(startIndex + count, this._internalRows.length) : this._internalRows.length;
            for (let i = startIndex; i < maxIndex; i++)
                yield this._internalRows[i];
        }
    }

    /**
     * Pokud zrovna probíhá načítání nebo reload dat, tak vrací promise, pomocí kterého jde na dočítání počkat, jinak vrací dokončený promise
     */
    public async waitForReload(_ct?: CancellationToken): Promise<void> {
        if (this._runningDataLoad != null)
            await this._runningDataLoad;
    }

    protected async _getClassDescription(): Promise<IOWClassDescription> {
        if (this.__classDescription === null)
            this.__classDescription = await ModelDescriptionsManager.instance.getClassInfo(this._className);
        return this.__classDescription;
    }

    /**
     *
     * @param params
     * @param cancellationToken
     */
    protected async _loadData(params: IDBDataSourceLoadParams, cancellationToken: CancellationToken): Promise<IRowData[]> {
        if (/@\d+/.test(this.condition) && (this.conditionParams == null || this.conditionParams.length === 0))
            return Promise.reject(new InvalidOperationException("Condition contains params but no parameter value available"));

        const startIndex = this._startIndex;

        if (startIndex === 0)
            this._allDataRead = false;
        else if (this._allDataRead)
            return Promise.resolve([]);

        const cdPromise = this._getClassDescription();
        const batchSize = params.batchSize !== undefined && params.batchSize > this._batchSize ? params.batchSize : this._batchSize;
        const count = this._maxRecordCount !== undefined ? this._maxRecordCount - this._startIndex : batchSize;

        const bcpStartIndex = this._startIndex;//uložíme start index pro případ kdy dočítání dat padne do výjimky
        this._startIndex += batchSize;

        this.loadingStarted.trigger(this, startIndex);

        this._runningDataLoadCTS = new CancellationTokenSource([cancellationToken]);
        this._runningDataLoad = getDataReaderClient().getDataRowsWithParams(this._className, this._attributes, this.condition, {
            params: this.conditionParams,
            orderBy: this._internalOrderBy,
            startIndex: startIndex,
            count,
            dataLength: this._dataLength,
            readVersioned: this._readVersioned,
            distinct: this.useDistinct,
            groupBy: this._groupBy,
        },
        this._fullLengthAttributes,
        this._getRequestContext(this._runningDataLoadCTS.token));

        let dsLoadInfo: IDataSourceLoadInfo;

        try {
            const rows = await this._runningDataLoad;

            if (batchSize === DATASOURCE_ALL_DATA)
                this._allDataRead = true;
            else
                this._allDataRead = (this._maxRecordCount !== undefined && this._startIndex + batchSize >= this._maxRecordCount) || rows.length < batchSize;

            params.startIndexForEvent = params.startIndexForEvent ?? startIndex;
            params.countForEvent = params.countForEvent ?? count;

            const cd = await cdPromise;
            if (cd.encryptingAllowed)
                await this._decryptData(rows, cd);

            dsLoadInfo = await this._processRows(params, cancellationToken, rows, startIndex);
            this._runningDataLoad = null;
            return rows;
        } catch (err) {
            this._runningDataLoad = null;
            this._startIndex = bcpStartIndex;//obnovíme původní start index, protože nedošlo k dočtení dat
            this.loadingCancelled.trigger(this, { isFiltered: false, emptyData: true, cancellationToken: cancellationToken });

            // zrušení nahrávání data je v pořádku
            if (!(err instanceof CancelError))
                this.error.trigger(this, err);

            return null;
        } finally {
            this.loadingEnded.trigger(this, dsLoadInfo ?? { isFiltered: false, fulltextSearchString: this.fulltextCondition?.searchString, emptyData: true, cancellationToken: cancellationToken });
        }
    }

    protected _isLastBatch(params: IDBDataSourceLoadParams): boolean {
        let isLastBatch = this._allDataRead;
        if (typeof params.countForEvent === "number") {
            if (params.countForEvent === DATASOURCE_ALL_DATA) {
                return true;
            }
            const startIndexForEvent = params.startIndexForEvent ?? 0;
            isLastBatch = isLastBatch && (startIndexForEvent + params.countForEvent) > this._internalRows.length;
        }

        return isLastBatch;
    }

    protected async _processRows(params: IDBDataSourceLoadParams, cancellationToken: CancellationToken, rows: IRowData[], startIndex: number): Promise<IDataSourceLoadInfo> {
        this._internalRows = startIndex === 0 ? rows : (this._internalRows || []).concat(rows);

        return this._afterProcessRow(params, cancellationToken);
    }

    protected _afterProcessRow(params: IDBDataSourceLoadParams, cancellationToken: CancellationToken): IDataSourceLoadInfo {
        const lastBatch = this._isLastBatch(params);
        const dsLoadInfo: IDataSourceLoadInfo = {
            startIndex: params.startIndexForEvent,
            dataGenerator: this._getIterator.bind(this, params.startIndexForEvent, params.countForEvent),
            lastBatch: lastBatch,
            isFiltered: this.hasFilterCondition,
            fulltextSearchString: this.fulltextCondition?.searchString,
            refreshedData: params.batchSize !== undefined,
            cancellationToken: cancellationToken,
            emptyData: false,
        };
        if (params.keepSelectedRecords != null)
            dsLoadInfo.keepSelectedRecords = params.keepSelectedRecords;

        this.dataLoaded.trigger(this, dsLoadInfo);

        return dsLoadInfo;
    }

    public async loadSpecificRecord(dataKey: string, selectLoaded: boolean = false, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData> {
        const resultRows = await this.loadSpecificRecords([dataKey], selectLoaded, cancellationToken);
        return resultRows && resultRows[0] || null;
    }

    public async loadSpecificRecords(dataKeys: string[], selectLoaded: boolean = false, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData[]> {
        if (!dataKeys || dataKeys.length === 0)
            return Promise.resolve([]);

        const newDataKeys = [];
        for (const dk of dataKeys) {
            const dataKey = dk.toString();
            if (this._specificRecordWaitList.includes(dataKey)) continue;
            else {
                this._specificRecordWaitList.push(dataKey);
                newDataKeys.push(dataKey);
            }
        }

        if (newDataKeys.length === 0) return Promise.resolve([]);
        dataKeys = newDataKeys;

        const loadedDataKeys: string[] = [];
        const loadedRows: IRowData[] = [];

        for (const row of this._internalRows) {
            const rowDataKeyRaw = AttrValueReader.read(row[this.dataKey]);
            const rowDataKey = rowDataKeyRaw && rowDataKeyRaw.toString();
            if (dataKeys.indexOf(rowDataKey) > -1) {
                loadedDataKeys.push(rowDataKey);
                loadedRows.push(row);
            }
        }

        const dataKeysToLoad = dataKeys.filter(dataKey => loadedDataKeys.indexOf(dataKey) === -1);

        if (dataKeysToLoad.length === 0) {
            this._specificRecordWaitList = []; //hledany zaznam je mezi doctenymi, ale nezobrazenymi radky. Musi se promazat, aby se v dalsim kole zjistilo, jestli je jiz zobrazeny
            return loadedRows;
        }

        const cdPromise = this._getClassDescription();

        this._runningDataLoadCTS = new CancellationTokenSource([cancellationToken]);
        this._runningDataLoad = getDataReaderClient().getDataRowsWithParams(this.className, this._attributes,
            StringUtils.combineConditions(ConditionOperator.and, this.condition, `${this.dataKey}=`.concat(dataKeysToLoad.join(` OR ${this.dataKey}=`))),
            { params: this.conditionParams },
            this._fullLengthAttributes,
            this._getRequestContext(this._runningDataLoadCTS.token))
            .then(async (rows) => {
                this._internalRows = [...rows, ...(this._internalRows || [])];

                for (const row of rows) {
                    if (this._specificRecordWaitList.includes(row[this.dataKey].toString())) {
                        this._specificRecordWaitList.splice(this._specificRecordWaitList.indexOf(row[this.dataKey].toString()), 1);
                    }
                }

                const cd = await cdPromise;
                if (cd.encryptingAllowed)
                    await this._decryptData(rows, cd);

                function* rowIterator(): Iterable<IRowData> {
                    for (const row of rows) {
                        yield row;
                    }
                }

                const eventArgs: IDataSourceLoadInfo = {
                    startIndex: 0,
                    dataGenerator: rowIterator,
                    lastBatch: false,
                    isFiltered: false,
                    fulltextSearchString: this.fulltextCondition?.searchString,
                    refreshedData: true,
                    keepExisting: true,
                    cancellationToken: cancellationToken,
                    emptyData: false,
                };
                if (selectLoaded)
                    eventArgs.selectLoaded = true;

                this._runningDataLoad = null;
                this._specificRecordWaitList = [];

                this.dataLoaded.trigger(this, eventArgs);
                this.loadingEnded.trigger(this, eventArgs);
                return rows;
            }).catch((err) => {
                this._runningDataLoad = null;
                this._specificRecordWaitList = [];
                this.loadingEnded.trigger(this, { isFiltered: false, fulltextSearchString: this.fulltextCondition?.searchString, emptyData: true, cancellationToken: cancellationToken });
                // zrušení nahrávání data je v pořádku
                if (!(err instanceof CancelError))
                    this.error.trigger(this, err);
            });

        return this._runningDataLoad;
    }
    /*
    * Vrací počet zadaných záznamů pro aktuálně nastavený filtr
    */
    public async count(cancellationToken: CancellationToken = CancellationToken.none): Promise<{ isFiltered: false; totalCount: number } | { filteredCount: number; isFiltered: true; totalCount: number }> {
        if (this.hasFilterCondition || this.hasFulltextCondition) {
            const baseCondition = StringUtils.combineConditions(ConditionOperator.and, this._initializeCondition, this._originalCondition);
            return {
                isFiltered: true,
                totalCount: await getDataReaderClient().getCount(this.className, baseCondition, this.conditionParams, this._readVersioned, this._getRequestContext(cancellationToken)),
                filteredCount: await getDataReaderClient().getCount(this.className, this.condition, this.conditionParams, this._readVersioned, this._getRequestContext(cancellationToken)),
            };
        } else {
            return {
                isFiltered: false,
                totalCount: await getDataReaderClient().getCount(this.className, this.condition, this.conditionParams, this._readVersioned, this._getRequestContext(cancellationToken)),
            };
        }
    }

    private async _decryptData(rows: readonly IRowData[], cd: IOWClassDescription, updateDisplayedData: boolean = false): Promise<void> {
        if (!rows || !cd.encryptingAllowed)
            return;

        for (const rowData of rows) {
            if (AttrValueReader.readRow(rowData, "Encrypted") !== true || AttrValueReader.readRow(rowData, "IsFinallyEncrypted") === true)
                continue;

            const pml = cd.passwordMasterLink;
            const id = pml && AttrValueReader.readRow(rowData, pml + ".ID") !== 0 ? AttrValueReader.readRow(rowData, pml + ".ID") : AttrValueReader.readRow(rowData, "ID");
            const className = pml && AttrValueReader.readRow(rowData, pml + ".ClassName") ? AttrValueReader.readRow(rowData, pml + ".ClassName") : AttrValueReader.readRow(rowData, "ClassName");

            if (PasswordManager.hasPassword(className, id)) {
                /*const encryptionKeyBase = PasswordManager.getPassword(className, id);
                const encryptionKey = await SecurityUtils.derivePasswordToAES256Key(encryptionKeyBase, rowData.ObjectID.toString());*/
                const encryptionKey = PasswordManager.getPassword(className, id);

                let promises: Promise<void>[] = [];
                const changedAttributes = new Map<string, any>();
                let decryptionSuccessful = false;
                for (const attr of this._attributes) {
                    const val = rowData[attr];
                    if (val instanceof IEncAttrValue && val.hasEncryptedValue) {
                        const prom = val.decrypt(encryptionKey).then((decryptionResult) => {
                            if (!decryptionResult)
                                return;

                            decryptionSuccessful = true;
                            if (updateDisplayedData)
                                changedAttributes.set(attr, val);
                        });
                        promises.push(prom);
                    }
                }

                await Promise.all(promises);

                if (!decryptionSuccessful)
                    return;

                promises = [];
                for (const attr of this._attributes) {
                    const val = rowData[attr];
                    if (val instanceof IEncAttrValue && !val.hasEncryptedValue) {
                        const prom = val.decrypt(encryptionKey).then((decryptionResult) => {
                            if (decryptionResult && updateDisplayedData)
                                changedAttributes.set(attr, val);
                        });
                        promises.push(prom);
                    }
                }

                await Promise.all(promises);

                if (changedAttributes.size > 0)
                    this.attributesChanged.trigger(this, {
                        key: rowData.ID,
                        isChanged: false,
                        values: changedAttributes,
                        isDecrypted: true,
                    });
            }
        }
    }

    /**
     * Smaže dočtené záznamy v datasource
     * */
    public clearData(): void {
        this._internalRows = [];
        const dsLoadInfo: IDataSourceLoadInfo = {
            startIndex: 0,
            dataGenerator: this._getIterator.bind(this, 0, 0),
            lastBatch: false,
            isFiltered: false,
            fulltextSearchString: this.fulltextCondition?.searchString,
            refreshedData: false,
            emptyData: true,
        };
        this.dataLoaded.trigger(this, dsLoadInfo);
    }

    /**
     * Nahraje data od zacatku
     */
    public loadNewData(keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData[]> {
        return this._loadNewData(undefined, keepSelectedRecords, cancellationToken);
    }

    protected _loadNewData(batchSize: number | undefined, keepSelectedRecords: boolean, cancellationToken: CancellationToken): Promise<IRowData[]> {
        cancellationToken.throwIfCancellationRequested();

        this._startIndex = 0;

        this._cancelLoading();

        const params: IDBDataSourceLoadParams = {};
        if (batchSize != null)
            params.batchSize = batchSize;
        if (keepSelectedRecords != null)
            params.keepSelectedRecords = keepSelectedRecords;

        return this._loadData(params, cancellationToken);
    }

    protected _cancelLoading(): void {
        // pokud se spustí nové dočítání dat a nějaké dočítání ještě běží, tak se to staré zruší a zahodí
        if (this._runningDataLoad && this._runningDataLoadCTS) {
            this._runningDataLoadCTS.cancel();
            this.loadingCancelled.trigger(this, { isFiltered: false });
        }
    }

    /**
    * Vyvola dotaz na dalsi varku dat
    */
    public getNextData(startIndex?: number, count?: number, keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<Iterable<IRowData>> {
        const inRange: boolean = (this._internalRows || []).length > (startIndex + count);
        const batchLoading = startIndex !== undefined && (inRange || this._allDataRead);

        if (!batchLoading && this._allDataRead && this.__internalRows.length <= startIndex) {
            return Promise.resolve([]);
        }

        if (!batchLoading && startIndex && startIndex > 0) {
            const params: IDBDataSourceLoadParams = { startIndexForEvent: startIndex, countForEvent: count };
            if (keepSelectedRecords != null)
                params.keepSelectedRecords = keepSelectedRecords;
            return this._loadData(params, cancellationToken);
        }

        let dsLoadInfo: IDataSourceLoadInfo;
        this.loadingStarted.trigger(this, undefined);
        try {
            if (batchLoading) {
                dsLoadInfo = {
                    startIndex: startIndex,
                    dataGenerator: this._getIterator.bind(this, startIndex, count),
                    lastBatch: !inRange,
                    isFiltered: this.hasFilterCondition,
                    fulltextSearchString: this.fulltextCondition?.searchString,
                    refreshedData: false,
                    cancellationToken: cancellationToken,
                    emptyData: false,
                };
                if (keepSelectedRecords != null)
                    dsLoadInfo.keepSelectedRecords = keepSelectedRecords;
                this.dataLoaded.trigger(this, dsLoadInfo);
                return Promise.resolve(this._getIterator.bind(this, startIndex, count)());
            } else {
                return Promise.resolve([]);
            }
        } finally {
            this.loadingEnded.trigger(this, dsLoadInfo ?? { isFiltered: this.hasFilterCondition, fulltextSearchString: this.fulltextCondition?.searchString, emptyData: true, cancellationToken: cancellationToken });
        }
    }

    /**
     * Vrací pole atributů, které datasource čte
     */
    public getAttrs(): string[] {
        return this._attributes;
    }
    /** Vrací pole atributů, jejichž hodnoty se nemají ořezávat */
    public getFullLengthAttrs(): string[] {
        return this._fullLengthAttributes;
    }

    /**
    * Nastavi atributy, ktere chci docist
    * @param attrs seznam atributu
    * @param fullLengthAttributes Seznam atributu, jejichž hodnoty se nemají ořezávat
    */
    public setAttrs(attrs: string[], fullLengthAttributes?: string[]): void {
        this._attributes = attrs;
        this._fullLengthAttributes = fullLengthAttributes || [];
    }

    /**
     * Nastaví atributy, ktere chci dočíst a dočte data, pokud přibyl nějaký nový atribut. Používat opatrně, pozor na vícenásobné načítání.
     * @param attrs seznam atributu
     * @param cancellationToken
     */
    public async setAttrsAndLoad(attrs: string[], cancellationToken: CancellationToken = CancellationToken.none): Promise<Iterable<IRowData>> {
        const oldAttr = this._attributes;
        this._attributes = attrs;

        if (!attrs.every((v) => oldAttr.indexOf(v) > -1))
            return await this.loadNewData(false, cancellationToken);

        return null;
    }

    public refreshData(fetchCount?: number, manualRefresh?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        return this._loadNewData(fetchCount, manualRefresh, cancellationToken);
    }

    public setFilterCondition(condition: string): void {
        this._filterCondition = condition;
        this._updateCondition();
    }

    public getFilterCondition(): string {
        return this._filterCondition;
    }

    /**
     * Vrací true, pokud je datasource filtrován.
     */
    public get hasFilterCondition(): boolean {
        return !StringUtils.isNullOrEmpty(this._filterCondition);
    }

    /** Text a podmínka pro fulltextové vyhledávání */
    public get fulltextCondition(): IFulltextSearchCondition {
        return this._fulltextCondition;
    }

    public set fulltextCondition(value: IFulltextSearchCondition) {
        this._fulltextCondition = value;
        this._updateCondition();
    }

    /** Vrací true, pokud je datasource fulltextově filtrován. */
    public get hasFulltextCondition(): boolean {
        return !StringUtils.isNullOrEmpty(this.fulltextCondition?.condition);
    }

    /**
     * Nastaví dodatečnou podmínku
     * @param conditionId Identifikátor dodatečné podmínky
     * @param condition Podmínka
     * @param params Parametry podmínky
     */
    public setAdditionalCondition(conditionId: string, condition: string, params?: ConditionParams): void {
        this._additionalConditions.set(conditionId, condition);

        if (/@\d+/.test(condition)) { // Můžeme jako vstup chtít i null, proto nekontrolujeme "params"
            this._additionalConditionsParam.set(conditionId, params);
        }
    }

    /**
     * Nastaví parametry pro dodatečnou podmínku
     * @param conditionId Identifikátor dodatečné podmínky
     * @param params Parametry podmínky
     */
    public setAdditionalConditionParams(conditionId: string, params: any[]): void {
        this._additionalConditionsParam.set(conditionId, params);
    }

    /**
     * Vrátí zda podmínka existuje, případně zda existuje alespoň jedna libovolná podmínka při prázdném vstupu
     * @param conditionId Identifikátor dodatečné podmínky
     */
    public hasAdditionalCondition(conditionId?: string): boolean {
        if (conditionId) {
            return this._additionalConditions.has(conditionId);
        }

        return this._additionalConditions.size > 0;
    }

    /**
     * Odebere dodatečnou podmínku
     * @param conditionId Identifikátor dodatečné podmínky
     */
    public removeAdditionalCondition(conditionId: string): void {
        if (this._additionalConditions.has(conditionId))
            this._additionalConditions.delete(conditionId);
        if (this._additionalConditionsParam.has(conditionId))
            this._additionalConditionsParam.delete(conditionId);
    }

    /** Zruší všechny dodatečné podmínky */
    public clearAdditionalConditions(): void {
        this._additionalConditions.clear();
        this._additionalConditionsParam.clear();
    }

    /**
     * Zkombinuje vsechny dostupne casti podminky a vytvori finalni podminku pro data reader
     */
    protected _updateCondition(): void {
        this._condition = StringUtils.combineConditions(ConditionOperator.and, this._initializeCondition, this._originalCondition, this._filterCondition, this.fulltextCondition?.condition ?? "");
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this._frameContext, cancellationToken);
    }

    //#region dispose
    /**
     * Destruktor
     */
    public dispose(): void {
        this._events?.dispose();
        this.dataLoaded.removeAll();
        this.error.removeAll();
        this.loadingCancelled.removeAll();
        this.loadingStarted.removeAll();
        this.loadingEnded.removeAll();
        this.attributesChanged.removeAll();
    }
    //#endregion dispose
}

/**
 * Parametry pro metodu _loadData
 */
export interface IDBDataSourceLoadParams {
    startIndexForEvent?: number;
    countForEvent?: number;
    batchSize?: number;
    keepSelectedRecords?: boolean;
}
